import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, Lock, ArrowRight, Sparkles, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, verifyTwoFactor } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [challengeId, setChallengeId] = useState(null);
  const [challengeType, setChallengeType] = useState(null);
  const [targetEmailHint, setTargetEmailHint] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setIsLoading(true);
    try {
      const result = await login(loginForm.email, loginForm.password);
      if (result?.two_factor_required) {
        setChallengeId(result.challenge_id);
        setChallengeType(result.challenge_type || 'email');
        setTargetEmailHint(result.target_email_hint || null);
        toast.success('Code de sécurité envoyé par email');
      } else {
        toast.success('Connexion réussie!');
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!challengeId || twoFactorCode.trim().length < 6) {
      toast.error('Entrez le code à 6 chiffres');
      return;
    }
    setIsLoading(true);
    try {
      const result = await verifyTwoFactor(challengeId, twoFactorCode);
      if (result?.two_factor_required) {
        setChallengeId(result.challenge_id);
        setChallengeType(result.challenge_type || 'totp');
        setTargetEmailHint(result.target_email_hint || null);
        setTwoFactorCode('');
        toast.success(result.message || 'Validation supplémentaire requise');
        return;
      }
      toast.success('Connexion sécurisée validée');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code invalide');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Hero Section */}
      <div 
        className="login-hero hidden lg:flex flex-col justify-center items-center p-12"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1765767056681-9583b29007cf?crop=entropy&cs=srgb&fm=jpg&q=85)',
        }}
      >
        <div className="relative z-10 text-center max-w-lg">
          <div className="flex items-center justify-center gap-3 mb-8">
            <img src="/logo.png" alt="RenoviaMail" className="w-44 h-44 drop-shadow-2xl" />
          </div>
          
          <p className="text-xl text-muted-foreground mb-8">
            La messagerie professionnelle nouvelle génération
          </p>
          
          <div className="grid gap-4 text-left">
            <div className="flex items-start gap-3 p-4 rounded-xl bg-card/50 backdrop-blur">
              <Sparkles className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-semibold">Interface Premium</p>
                <p className="text-sm text-muted-foreground">Design élégant et moderne</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 rounded-xl bg-card/50 backdrop-blur">
              <Sparkles className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-semibold">Organisation Intelligente</p>
                <p className="text-sm text-muted-foreground">Dossiers, labels et recherche avancée</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 rounded-xl bg-card/50 backdrop-blur">
              <Sparkles className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-semibold">Sécurité Maximale</p>
                <p className="text-sm text-muted-foreground">Vos données protégées</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="login-form-container">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img src="/logo.png" alt="RenoviaMail" className="w-40 h-40 drop-shadow-2xl" />
          </div>

          <Card className="border-border/50 bg-card/50 backdrop-blur">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl">Connexion</CardTitle>
              <CardDescription>
                Entrez vos identifiants de messagerie
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={challengeId ? handleVerifyCode : handleLogin} className="space-y-4">
                {challengeId && (
                  <div className="rounded-xl border border-primary/30 bg-primary/10 p-3 text-sm text-foreground">
                    <div className="flex items-center gap-2 font-semibold mb-1">
                      <ShieldCheck className="w-4 h-4 text-primary" />
                      Vérification de sécurité
                    </div>
                    <p className="text-muted-foreground">
                      {challengeType === 'totp'
                        ? 'Entrez le code de votre application Authenticator.'
                        : challengeType === 'approval'
                          ? `Connexion inhabituelle: entrez le code envoyé${targetEmailHint ? ` à ${targetEmailHint}` : ' par email'}.`
                          : `Un code à 6 chiffres vient d'être envoyé${targetEmailHint ? ` à ${targetEmailHint}` : ' par email'}.`}
                    </p>
                  </div>
                )}
                {!challengeId && (
                  <>
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="contact@renoviapro.fr"
                      className="pl-10"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="login-password">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                  </>
                )}
                {challengeId && (
                  <div className="space-y-2">
                    <Label htmlFor="login-code">{challengeType === 'totp' ? 'Code Authenticator' : 'Code de sécurité'}</Label>
                    <div className="relative">
                      <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="login-code"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="123456"
                        className="pl-10 tracking-[0.35em] text-center"
                        maxLength={6}
                        value={twoFactorCode}
                        onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      />
                    </div>
                  </div>
                )}
                
                <Button 
                  type="submit" 
                  className="w-full btn-gold-glow" 
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading ? 'Connexion...' : challengeId ? 'Valider le code' : 'Se connecter'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                {challengeId && (
                  <button
                    type="button"
                    onClick={() => { setChallengeId(null); setChallengeType(null); setTargetEmailHint(null); setTwoFactorCode(''); }}
                    className="w-full text-xs text-muted-foreground hover:text-foreground"
                  >
                    Revenir à la saisie du mot de passe
                  </button>
                )}
                
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Utilisez vos identifiants de messagerie professionnelle
                </p>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-6">
            © 2026 RenoviaMail - Tous droits réservés
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
